import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon, { type IconName } from '../components/Icon';
import './Pages.css';

/* ── Animated counter hook ────────────────────────────── */
function useCountUp(target: number, duration = 1600, start = false) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (ts: number) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setVal(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return val;
}

/* ── Animated stat card ───────────────────────────────── */
function StatCard({ value, prefix, suffix, icon, label, desc, delay }: {
  value: number; prefix: string; suffix: string; icon: IconName; label: string; desc: string; delay: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  const count = useCountUp(value, 1400, inView);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.4 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={ref} className="impact-stat reveal" style={{ transitionDelay: `${delay}ms` }}>
      <div className="impact-stat__icon">
        <Icon name={icon} size={22} />
      </div>
      <div className="impact-stat__value">
        {prefix}{count}{suffix}
      </div>
      <div className="impact-stat__label">{label}</div>
      <p>{desc}</p>
    </div>
  );
}

/* ── User card with icon label map ───────────────────── */
const userCards = [
  { icon: 'mountain', name: 'Remote communities', desc: 'Beyond reliable cellular coverage.', color: '#d8232a' },
  { icon: 'tree', name: 'Trekkers & hikers', desc: 'Routes where mobile signal disappears.', color: '#33457a' },
  { icon: 'flame', name: 'Disaster-prone zones', desc: 'Landslides, floods, earthquakes.', color: '#e54a51' },
  { icon: 'users', name: 'Local governments', desc: 'Municipalities, ward offices, EM teams.', color: '#1a2849' },
  { icon: 'shield', name: 'Search & rescue', desc: 'Receive and manage remote alerts.', color: '#b01a20' },
  { icon: 'globe', name: 'Tourism operators', desc: 'Hotels, resorts, trekking agencies.', color: '#33457a' },
];

/* ── Impact list items with icons ────────────────────── */
const impactItems = [
  { icon: 'zap', text: 'Faster emergency notification across remote areas' },
  { icon: 'eye', text: 'Better visibility of incidents invisible to traditional channels' },
  { icon: 'message', text: 'Improved coordination between responders' },
  { icon: 'wifi-off', text: 'Reduced dependency on fragile cellular infrastructure' },
  { icon: 'shield', text: 'Increased safety for remote communities and travelers' },
  { icon: 'layers', text: 'Stronger disaster preparedness at the ward level' },
  { icon: 'lightning', text: 'Affordable infrastructure for resource-constrained regions' },
];

/* ── Nepal SVG illustration ───────────────────────────── */
function NepalMapIllustration() {
  return (
    <svg viewBox="0 0 480 320" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', maxWidth: 480 }}>
      {/* Sky */}
      <rect width="480" height="320" fill="#0f1a35" rx="16" />
      {/* Stars */}
      {[
        [40, 30], [90, 55], [160, 25], [230, 45], [310, 20], [390, 50], [440, 30],
        [60, 80], [140, 70], [270, 60], [350, 75], [420, 65],
      ].map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={i % 3 === 0 ? 1.5 : 1} fill="white" opacity={0.4 + (i % 4) * 0.15} />
      ))}

      {/* Mountain silhouettes */}
      <path d="M0 260 L60 180 L110 210 L160 150 L220 200 L280 130 L340 175 L400 140 L450 170 L480 155 L480 320 L0 320 Z" fill="#1a2849" opacity="0.7" />
      <path d="M0 300 L80 240 L150 270 L210 230 L290 260 L360 220 L420 250 L480 230 L480 320 L0 320 Z" fill="#1a2849" opacity="0.9" />

      {/* Gateway tower */}
      <rect x="295" y="160" width="6" height="50" fill="#33457a" />
      <circle cx="298" cy="158" r="6" fill="#d8232a" />
      <circle cx="298" cy="158" r="10" stroke="#d8232a" strokeWidth="1" opacity="0.4" />
      <circle cx="298" cy="158" r="16" stroke="#d8232a" strokeWidth="0.8" opacity="0.25" />

      {/* SOS device / person */}
      <circle cx="145" cy="200" r="10" fill="#d8232a" opacity="0.9" />
      <circle cx="145" cy="200" r="16" stroke="#d8232a" strokeWidth="1.2" opacity="0.4" />
      <circle cx="145" cy="200" r="26" stroke="#d8232a" strokeWidth="0.8" opacity="0.2" />
      {/* SOS text */}
      <text x="140" y="204" fontSize="6" fill="white" fontFamily="sans-serif" fontWeight="700">SOS</text>

      {/* Signal arc from device to gateway */}
      <path d="M145 200 Q 220 160 298 158" stroke="#d8232a" strokeWidth="1.5" strokeDasharray="4 4" opacity="0.5" />
      {/* Gateway to cloud */}
      <path d="M298 158 Q 330 120 380 100" stroke="#33457a" strokeWidth="1.5" strokeDasharray="4 4" opacity="0.4" />

      {/* Cloud / platform */}
      <rect x="360" y="60" width="80" height="55" rx="8" fill="#243463" opacity="0.8" />
      <rect x="364" y="64" width="72" height="8" rx="3" fill="#33457a" opacity="0.6" />
      <rect x="364" y="76" width="52" height="5" rx="2" fill="#33457a" opacity="0.4" />
      <rect x="364" y="85" width="40" height="5" rx="2" fill="#33457a" opacity="0.3" />
      <rect x="364" y="94" width="55" height="16" rx="3" fill="#d8232a" opacity="0.15" />
      <text x="368" y="106" fontSize="7" fill="#d8232a" fontFamily="monospace" fontWeight="700">SOS ALERT</text>

      {/* Second SOS device lower left */}
      <circle cx="80" cy="235" r="7" fill="#d8232a" opacity="0.7" />
      <circle cx="80" cy="235" r="12" stroke="#d8232a" strokeWidth="0.8" opacity="0.3" />
      <text x="76" y="238" fontSize="4.5" fill="white" fontFamily="sans-serif" fontWeight="700">SOS</text>

      {/* Label */}
      <text x="16" y="310" fontSize="9" fill="white" fontFamily="sans-serif" opacity="0.5">SETU: Nepal Coverage Map</text>
    </svg>
  );
}

/* ── Case study illustration ─────────────────────────── */
function CaseStudyIllustration() {
  return (
    <svg viewBox="0 0 500 340" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', maxWidth: 500 }}>
      {/* Background */}
      <rect width="500" height="340" rx="16" fill="#142244" />

      {/* Rain effect */}
      {[...Array(30)].map((_, i) => (
        <line key={i}
          x1={(i * 17) + (i % 3) * 5}
          y1={20 + (i * 11) % 100}
          x2={(i * 17) + (i % 3) * 5 - 3}
          y2={30 + (i * 11) % 100}
          stroke="white" strokeWidth="0.8" opacity="0.15"
        />
      ))}

      {/* Mountain with landslide */}
      <path d="M0 340 L100 200 L150 230 L200 140 L260 190 L310 120 L380 180 L440 140 L500 170 L500 340 Z" fill="#1a2849" opacity="0.8" />
      {/* Landslide debris */}
      <path d="M310 120 L280 200 L250 260 L200 220 Z" fill="#243463" opacity="0.6" />
      <path d="M200 140 L160 210 L220 240 L200 200 Z" fill="#243463" opacity="0.5" />
      {/* Landslide cracks */}
      <path d="M310 120 L295 150 L280 175" stroke="#ef7a7f" strokeWidth="1" opacity="0.4" />
      <path d="M260 190 L270 210 L290 235" stroke="#ef7a7f" strokeWidth="0.8" opacity="0.3" />

      {/* Person in danger */}
      <circle cx="235" cy="215" r="8" fill="#d8232a" />
      <circle cx="235" cy="215" r="14" stroke="#d8232a" strokeWidth="1.5" opacity="0.4" />
      <circle cx="235" cy="215" r="22" stroke="#d8232a" strokeWidth="0.8" opacity="0.2" />
      <text x="230" y="218" fontSize="5.5" fill="white" fontFamily="sans-serif" fontWeight="700">SOS</text>

      {/* Ruined tower */}
      <rect x="185" y="205" width="5" height="35" fill="#33457a" transform="rotate(15 185 240)" opacity="0.6" />
      <circle cx="190" cy="200" r="5" fill="#33457a" opacity="0.4" />

      {/* SETU solution flow */}
      <rect x="340" y="60" width="130" height="180" rx="10" fill="#243463" opacity="0.7" />
      <rect x="348" y="68" width="114" height="8" rx="3" fill="#33457a" opacity="0.5" />
      <text x="356" y="74" fontSize="6" fill="white" fontFamily="monospace" opacity="0.6">SETU DASHBOARD</text>

      {/* Timeline steps */}
      {[
        { y: 88, color: '#d8232a', label: '01 SOS TRIGGERED', time: '00:00' },
        { y: 118, color: '#ef7a7f', label: '02 GATEWAY RECEIVED', time: '00:12' },
        { y: 148, color: '#f59e0b', label: '03 ALERT ACKNOWLEDGED', time: '00:38' },
        { y: 178, color: '#3b82f6', label: '04 TEAM DISPATCHED', time: '02:15' },
        { y: 208, color: '#10b981', label: '05 INCIDENT RESOLVED', time: '11:40' },
      ].map((step) => (
        <g key={step.label}>
          <rect x="348" y={step.y} width="114" height="22" rx="5" fill={step.color} opacity="0.15" />
          <rect x="348" y={step.y} width="4" height="22" rx="2" fill={step.color} opacity="0.8" />
          <text x="358" y={step.y + 10} fontSize="6.5" fill="white" fontFamily="sans-serif" fontWeight="600">{step.label}</text>
          <text x="440" y={step.y + 10} fontSize="6.5" fill={step.color} fontFamily="monospace">{step.time}</text>
        </g>
      ))}

      {/* Comparison bar */}
      <rect x="348" y="250" width="114" height="4" rx="2" fill="#33457a" opacity="0.5" />
      <rect x="348" y="250" width="20" height="4" rx="2" fill="#d8232a" />
      <text x="348" y="268" fontSize="6" fill="white" fontFamily="sans-serif" opacity="0.5">Today with SETU</text>
      <text x="348" y="280" fontSize="6" fill="#d8232a" fontFamily="sans-serif" fontWeight="600">~3 min avg response</text>

      <rect x="348" y="298" width="114" height="4" rx="2" fill="#33457a" opacity="0.5" />
      <rect x="348" y="298" width="90" height="4" rx="2" fill="#6e6e6a" />
      <text x="348" y="316" fontSize="6" fill="white" fontFamily="sans-serif" opacity="0.5">Without SETU</text>
      <text x="348" y="328" fontSize="6" fill="#6e6e6a" fontFamily="sans-serif">Hours, or no response</text>
    </svg>
  );
}

export default function Impact() {
  return (
    <div className="page">
      {/* ── HERO ──────────────────────────────────────── */}
      <section className="page-hero page-hero--dark page-hero--impact">
        <div className="page-hero__map-bg" aria-hidden>
          <svg viewBox="0 0 1200 500" preserveAspectRatio="xMidYMid slice">
            <defs>
              <radialGradient id="ig" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#d8232a" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#d8232a" stopOpacity="0" />
              </radialGradient>
              <radialGradient id="ibg" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#33457a" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#33457a" stopOpacity="0" />
              </radialGradient>
              <linearGradient id="terr" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#1a2849" stopOpacity="0.15" />
                <stop offset="100%" stopColor="#1a2849" stopOpacity="0.04" />
              </linearGradient>
            </defs>
            {/* terrain */}
            <path d="M0 420 L80 340 L160 380 L240 280 L340 350 L440 260 L560 320 L680 230 L780 290 L880 240 L980 300 L1080 260 L1200 300 L1200 500 L0 500 Z" fill="url(#terr)" />
            <path d="M0 480 L100 420 L200 455 L300 400 L420 450 L540 390 L660 440 L780 390 L900 440 L1020 410 L1140 455 L1200 430 L1200 500 L0 500 Z" fill="#1a2849" fillOpacity="0.1" />

            {/* SOS pulse at device location */}
            <circle cx="340" cy="350" r="10" fill="url(#ig)" />
            <circle cx="340" cy="350" r="24" stroke="#d8232a" strokeWidth="1.5" strokeOpacity="0.35" />
            <circle cx="340" cy="350" r="48" stroke="#d8232a" strokeWidth="1" strokeOpacity="0.2" />
            <circle cx="340" cy="350" r="80" stroke="#d8232a" strokeWidth="0.7" strokeOpacity="0.1" />

            {/* Device dot */}
            <circle cx="340" cy="350" r="7" fill="#d8232a" />

            {/* Signal path */}
            <path d="M340 350 Q 560 280 780 220" stroke="#d8232a" strokeWidth="1.5" strokeDasharray="5 5" fill="none" strokeOpacity="0.5" />

            {/* Gateway */}
            <rect x="776" y="206" width="8" height="24" fill="#33457a" />
            <circle cx="780" cy="204" r="6" fill="#33457a" />
            <circle cx="780" cy="204" r="12" stroke="#33457a" strokeWidth="1.2" strokeOpacity="0.4" />
            <circle cx="780" cy="204" r="22" stroke="#33457a" strokeWidth="0.8" strokeOpacity="0.2" />

            {/* Second path to cloud */}
            <path d="M780 204 Q 900 140 1040 100" stroke="#33457a" strokeWidth="1" strokeDasharray="4 4" fill="none" strokeOpacity="0.3" />

            {/* Cloud */}
            <rect x="1000" y="60" width="120" height="70" rx="10" fill="#243463" opacity="0.5" />
            <rect x="1008" y="68" width="104" height="8" rx="3" fill="#33457a" opacity="0.4" />
            <rect x="1008" y="82" width="70" height="5" rx="2" fill="#33457a" opacity="0.3" />
            <rect x="1008" y="92" width="90" height="16" rx="4" fill="#d8232a" opacity="0.2" />
            <text x="1012" y="104" fontSize="9" fill="#d8232a" fontFamily="monospace" fontWeight="700">SOS ALERT</text>

            {/* Distant SOS */}
            <circle cx="160" cy="420" r="5" fill="#d8232a" opacity="0.5" />
            <circle cx="160" cy="420" r="12" stroke="#d8232a" strokeWidth="0.8" strokeOpacity="0.25" />
          </svg>
        </div>

        <div className="container page-hero__impact-inner">
          <div className="page-hero__text">
            <span className="eyebrow">Social Impact</span>
            <h1>Closing the communication gap, for the people who need it most.</h1>
            <p className="lede">
              SETU is a people-centered technology project. Its purpose is not to deploy IoT
              devices for the sake of technology. It is to reduce the gap between a person in
              danger and the people capable of helping them.
            </p>
            <div className="page-hero__cta">
              <Link to="/technology" className="btn btn--primary btn--lg">
                See how it works
                <Icon name="arrow-right" size={18} className="arrow" />
              </Link>
              <Link to="/contact" className="btn btn--ghost btn--lg">
                Partner with us
              </Link>
            </div>
          </div>
          <div className="page-hero__visual reveal">
            <NepalMapIllustration />
          </div>
        </div>
      </section>

      {/* ── STATS ──────────────────────────────────────── */}
      <section className="section">
        <div className="container">
          <div className="section__head">
            <div className="reveal">
              <span className="eyebrow">By the numbers</span>
              <h2>Designed to make a measurable difference.</h2>
            </div>
            <p className="lede reveal reveal-delay-1">
              Real impact begins with realistic engineering. Every number below is grounded in
              the technical constraints and design choices that define SETU.
            </p>
          </div>
          <div className="impact-grid">
            {[
              { value: 3, prefix: '<', suffix: ' min', icon: 'clock' as IconName, label: 'Faster alerts', desc: 'From isolated device to first responder acknowledgement.' },
              { value: 0, prefix: '', suffix: ' SIM', icon: 'wifi-off' as IconName, label: 'cards required', desc: 'Devices do not depend on cellular subscriptions.' },
              { value: 15, prefix: '', suffix: '+ km', icon: 'map' as IconName, label: 'Coverage per gateway', desc: 'Range depends on terrain and antenna configuration.' },
              { value: 24, prefix: '', suffix: '/7', icon: 'battery' as IconName, label: 'Standby operation', desc: 'Battery-powered endpoints with solar gateways.' },
            ].map((s, i) => (
              <StatCard key={s.label} {...s} delay={i * 80} />
            ))}
          </div>
        </div>
      </section>

      {/* ── TARGET USERS ───────────────────────────────── */}
      <section className="section section--soft">
        <div className="container">
          <div className="section__head">
            <div className="reveal">
              <span className="eyebrow">Target users</span>
              <h2>Built for the people who most need resilient communication.</h2>
            </div>
            <p className="lede reveal reveal-delay-1">
              SETU serves a wide spectrum of users across rural municipalities, trekking routes,
              and disaster-prone regions of Nepal.
            </p>
          </div>
          <div className="users-grid">
            {userCards.map((u, i) => (
              <div key={u.name} className="user-card reveal" style={{ transitionDelay: `${i * 60}ms` }}>
                <div className="user-card__icon" style={{ background: u.color + '18', borderColor: u.color + '30', color: u.color }}>
                  <Icon name={u.icon as any} size={22} />
                </div>
                <div>
                  <strong>{u.name}</strong>
                  <span>{u.desc}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CASE STUDY ─────────────────────────────────── */}
      <section className="section">
        <div className="container">
          <div className="case-study reveal">
            <div className="case-study__story">
              <span className="eyebrow">Real-world scenario</span>
              <h2>The 2025 Rasuwa flood: when infrastructure failed at the worst moment.</h2>
              <p className="lede">
                In such situations, the question is not simply <em>"Can we send an emergency message?"</em>
                The more important question is: <strong style={{ color: 'var(--accent)' }}>Can we send one
                when the infrastructure normally used to send it has failed?</strong>
              </p>
              <p>
                The Rasuwa district flood of 2025 demonstrated how quickly cellular towers go dark
                during monsoon disasters, precisely when communication is most critical. SETU's
                LoRa-based architecture is designed to remain operational in exactly these conditions:
                off-grid, battery-powered, and independent of damaged infrastructure.
              </p>
              <div className="case-study__stats">
                <div className="case-study__stat">
                  <strong>0</strong>
                  <span>Towers needed for SOS</span>
                </div>
                <div className="case-study__stat">
                  <strong>~3 min</strong>
                  <span>Avg. alert-to-response</span>
                </div>
                <div className="case-study__stat">
                  <strong>15+ km</strong>
                  <span>Valley coverage radius</span>
                </div>
              </div>
            </div>
            <div className="case-study__visual reveal reveal-delay-2">
              <CaseStudyIllustration />
            </div>
          </div>
        </div>
      </section>

      {/* ── IMPACT LIST ───────────────────────────────── */}
      <section className="section section--dark">
        <div className="container">
          <div className="section__head">
            <div className="reveal">
              <span className="eyebrow">Why this matters for Nepal</span>
              <h2>The long-term impact of resilient emergency infrastructure.</h2>
            </div>
            <p className="lede reveal reveal-delay-1">
              Beyond individual emergencies, SETU builds systemic resilience, creating a
              communication layer that serves communities before, during, and after disasters.
            </p>
          </div>
          <div className="impact-list">
            {impactItems.map((item, i) => (
              <div key={item.text} className="impact-item reveal" style={{ transitionDelay: `${i * 50}ms` }}>
                <div className="impact-item__icon">
                  <Icon name={item.icon as any} size={18} />
                </div>
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────── */}
      <section className="section cta-section">
        <div className="container">
          <div className="impact-cta reveal">
            <div className="impact-cta__text">
              <span className="eyebrow">Get involved</span>
              <h2>Bring SETU to your community.</h2>
              <p className="lede">
                Municipalities, tourism operators, NGOs, and development partners can help deploy
                SETU gateways at the edges of cellular coverage, turning dead zones into
                connected, safer communities.
              </p>
            </div>
            <div className="impact-cta__actions">
              <Link to="/contact" className="btn btn--primary btn--lg">
                Start a deployment
                <Icon name="arrow-right" size={18} className="arrow" />
              </Link>
              <Link to="/features" className="btn btn--ghost btn--lg">
                Explore all features
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
